import * as React from "react";
import classNames from "classnames";

import css from "./Pagination.module.scss";

export default function Pagination({
  currentPage,
  totalPages,
  extraParams,
}: {
  currentPage: number;
  totalPages: number;
  extraParams?: Record<string, string>;
}) {
  const extraParamsString = React.useMemo(() => {
    if (!extraParams) return "";

    const entries = Object.entries(extraParams);

    // avoid calling .length on extraParams directly
    if (!entries.length) return "";

    // avoid using URLSearchParams API for compatibilty for server/client rendering
    return "&" + entries.map((pair) => pair.join("=")).join("&");
  }, [extraParams]);

  const buttons = React.useMemo(() => {
    const collexion = [];

    // If there are masses of pages, we don't want to actually render them all - we'll render the
    // first "maxPagesToRender", and then ellipsis, then the last page number
    const maxPagesToRender = 7;
    const showEllipsis = totalPages > maxPagesToRender;

    const toRender = showEllipsis ? maxPagesToRender : totalPages;

    for (let i = 1; i <= toRender; i++) {
      let btn;

      if (i === currentPage) {
        btn = (
          <div key={i} className={classNames(css.current, css.disabled)}>
            {i}
          </div>
        );
      } else {
        btn = (
          <a key={i} href={`?page=${i}${extraParamsString}`}>
            {i}
          </a>
        );
      }

      collexion.push(btn);
    }

    if (showEllipsis) {
      collexion.push(
        <div key="ellipsis" className={css.ellipsis}>
          …
        </div>,
      );
      collexion.push(
        <a key={totalPages} href={`?page=${totalPages}${extraParamsString}`}>
          {totalPages}
        </a>,
      );
    }

    return collexion;
  }, [currentPage, totalPages, extraParamsString]);

  const prevBtn = React.useMemo(() => {
    if (currentPage === 1) {
      return <div className={css.disabled}>&lt;</div>;
    } else {
      return <a href={`?page=${currentPage - 1}${extraParamsString}`}>&lt;</a>;
    }
  }, [currentPage, extraParamsString]);

  const nextBtn = React.useMemo(() => {
    if (currentPage === totalPages) {
      return <div className={css.disabled}>&gt;</div>;
    } else {
      return <a href={`?page=${currentPage + 1}${extraParamsString}`}>&gt;</a>;
    }
  }, [currentPage, totalPages, extraParamsString]);

  return (
    <div className={css.pagination}>
      {!!buttons.length && prevBtn}
      {buttons}
      {!!buttons.length && nextBtn}
    </div>
  );
}
